// ============================================================================
// REVIEW QUEUE — sortable, filterable, paginated table for booking review
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  ClipboardCheck, Loader, AlertCircle, CheckCircle2, XCircle,
  X, History, ArrowUp, ArrowDown, ChevronsUpDown, Search,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import {
  listReviewQueue,
  listAllBookings,
  reviewBooking,
  cancelBooking,
  retryOutlookSync,
  StudioBooking,
  BookingStatus,
  BookingDateType,
  FacilitatorTier,
  DATE_TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  TIER_LABELS
} from '../services/studio-bookings';

// ============================================================================
// TYPES
// ============================================================================

type SortCol = 'status' | 'customer' | 'requestedBy' | 'room' | 'date' | 'eventType' | 'facilitator';
type SortDir = 'asc' | 'desc';
interface SortState { col: SortCol; dir: SortDir }

interface Filters {
  status: string;
  customer: string;
  requestedBy: string;
  room: string;
  eventType: string;
}

const PAGE_SIZES = [10, 25, 50];

// ============================================================================
// HELPERS
// ============================================================================

const STATUS_DOT: Record<BookingStatus, string> = {
  pending:   'bg-amber-400',
  approved:  'bg-green-500',
  rejected:  'bg-red-500',
  cancelled: 'bg-slate-400'
};

const STATUS_ORDER: Record<BookingStatus, number> = {
  pending: 0, approved: 1, rejected: 2, cancelled: 3
};

const DATE_TYPE_SHORT: Record<BookingDateType, string> = {
  morning:   'Morning',
  afternoon: 'Afternoon',
  full_day:  'Full Day',
  multi_day: 'Multi-day'
};

function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
}

function applyFilters(bookings: StudioBooking[], f: Filters): StudioBooking[] {
  return bookings.filter(b => {
    if (f.status && b.status !== f.status) return false;
    if (f.customer && !b.customer.toLowerCase().includes(f.customer.toLowerCase())) return false;
    if (f.requestedBy && !b.requestedByName.toLowerCase().includes(f.requestedBy.toLowerCase())) return false;
    if (f.room && b.roomName !== f.room) return false;
    if (f.eventType && b.eventTypeName !== f.eventType) return false;
    return true;
  });
}

function applySort(bookings: StudioBooking[], sort: SortState | null): StudioBooking[] {
  if (!sort) return bookings;
  const mult = sort.dir === 'asc' ? 1 : -1;
  return [...bookings].sort((a, b) => {
    switch (sort.col) {
      case 'status':      return mult * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
      case 'customer':    return mult * a.customer.localeCompare(b.customer);
      case 'requestedBy': return mult * a.requestedByName.localeCompare(b.requestedByName);
      case 'room':        return mult * a.roomName.localeCompare(b.roomName);
      case 'date':        return mult * a.startDate.localeCompare(b.startDate);
      case 'eventType':   return mult * a.eventTypeName.localeCompare(b.eventTypeName);
      case 'facilitator': return mult * a.facilitatorName.localeCompare(b.facilitatorName);
      default: return 0;
    }
  });
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function getCustomerStatusLabel(status: string): string {
  if (status === 'internal') return 'Internal';
  if (status === 'new') return 'New customer';
  return 'Existing customer';
}

function getBillingLabel(type: string): string {
  if (type === 'investment') return 'Investment';
  if (type === 'na') return 'N/A';
  return 'Chargeable';
}

function FacilitatorCell({ name, tier }: { name: string; tier: FacilitatorTier }) {
  const isAbsent = !name || name === 'N/A' || name === 'TBC';
  if (isAbsent) {
    return <span className="text-xs text-slate-400 italic">{name === 'TBC' ? 'TBC' : 'Not required'}</span>;
  }
  return (
    <>
      <div>{name}</div>
      <div className="text-xs text-slate-400">{TIER_LABELS[tier]}</div>
    </>
  );
}

function StatusBadge({ status }: { status: BookingStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
      {STATUS_LABELS[status]}
    </span>
  );
}

// The requester booked over a meeting already in the room's Outlook calendar.
function OutlookConflictOverrideBadge({ booking }: { booking: StudioBooking }) {
  if (!booking.outlookConflictOverride) return null;
  return (
    <div className="mt-1">
      <span
        title={`${booking.outlookConflictOverride.overriddenByName} chose to book over an existing Outlook meeting in this slot`}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border text-amber-700 bg-amber-50 border-amber-200"
      >
        <AlertCircle className="w-3 h-3" /> Overlaps Outlook meeting
      </span>
    </div>
  );
}

// Orthogonal to BookingStatus — a booking can be approved/cancelled in our
// system but not actually reflected in Outlook if the Graph write failed.
function OutlookSyncBadge({ booking, showActions, onRetry, retrying }: {
  booking: StudioBooking; showActions: boolean; onRetry: () => void; retrying: boolean;
}) {
  if (booking.outlookSyncStatus !== 'failed') return null;
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <span
        title={booking.outlookSyncError || 'Outlook sync failed'}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border text-amber-700 bg-amber-50 border-amber-200"
      >
        <AlertCircle className="w-3 h-3" /> Not synced to Outlook
      </span>
      {showActions && (
        <button
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-60 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} /> {retrying ? 'Retrying…' : 'Retry sync'}
        </button>
      )}
    </div>
  );
}

function SortIcon({ col, sort }: { col: SortCol; sort: SortState | null }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
  return sort.dir === 'asc'
    ? <ArrowUp className="w-3.5 h-3.5 text-primary" />
    : <ArrowDown className="w-3.5 h-3.5 text-primary" />;
}

function ColHeader({ col, label, sort, onSort }: {
  col: SortCol; label: string; sort: SortState | null; onSort: (c: SortCol) => void;
}) {
  return (
    <th
      className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 cursor-pointer select-none hover:text-slate-600 transition-colors whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      <div className="flex items-center gap-1.5">{label}<SortIcon col={col} sort={sort} /></div>
    </th>
  );
}

function ReviewModal({ booking, action, onClose, onDone }: {
  booking: StudioBooking; action: 'approved' | 'rejected'; onClose: () => void; onDone: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outlookConflict, setOutlookConflict] = useState<string | null>(null);
  const isApprove = action === 'approved';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isApprove && !notes.trim()) { setError('Please provide a reason for rejection.'); return; }
    setSubmitting(true);
    try {
      await reviewBooking(booking.bookingId, action, notes.trim() || undefined);
      onDone();
    } catch (err: any) {
      if (err.response?.status === 424 && err.response?.data?.outlookSyncFailed) {
        setOutlookConflict(err.response.data.error || 'Could not connect to Outlook.');
      } else {
        setError(err.response?.data?.error || 'Failed to submit review');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForceApprove() {
    setSubmitting(true);
    try {
      await reviewBooking(booking.bookingId, 'approved', notes.trim() || undefined, true);
      onDone();
    } catch (err: any) {
      setOutlookConflict(null);
      setError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  }

  const dateLabel = booking.startDate === booking.endDate
    ? formatDate(booking.startDate)
    : `${formatDate(booking.startDate)} → ${formatDate(booking.endDate)}`;

  let submitLabel: string;
  if (submitting) submitLabel = 'Submitting…';
  else if (isApprove) submitLabel = 'Approve';
  else submitLabel = 'Reject';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-[#e2e8f0]">
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <h2 className="font-bold text-navy text-lg">{isApprove ? 'Approve Booking' : 'Reject Booking'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          <div className="bg-slate-50 rounded-xl p-3 mb-4 text-sm text-slate-500">
            <span className="font-semibold text-navy">{booking.customer}</span>
            {' · '}{booking.roomName}{' · '}{DATE_TYPE_LABELS[booking.dateType]}
            {' on '}{dateLabel}
          </div>
          {outlookConflict ? (
            <div>
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Couldn't connect to Outlook</p>
                  <p className="mt-0.5">{outlookConflict}</p>
                  <p className="mt-1 text-amber-700">
                    You can approve anyway — the room won't be held in Outlook until sync is retried.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={onClose}
                  className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button type="button" onClick={handleForceApprove} disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition-all shadow-sm bg-amber-600 hover:bg-amber-700 shadow-amber-200">
                  {submitting ? 'Approving…' : 'Approve anyway'}
                </button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <label className="block text-sm font-semibold text-navy mb-2">
              Notes{' '}
              {isApprove
                ? <span className="text-slate-400 font-normal">(optional)</span>
                : <span className="text-red-500">*</span>}
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={isApprove ? 'Any notes for the requester…' : 'Reason for rejection…'}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <div className="flex gap-3 mt-4">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition-all shadow-sm ${
                  isApprove ? 'bg-green-600 hover:bg-green-700 shadow-green-200' : 'bg-red-600 hover:bg-red-700 shadow-red-200'
                }`}>
                {submitLabel}
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}

function RowActions({
  variant, onApprove, onReject, showCancelConfirm, onCancelClick, onCancelConfirm, onCancelDismiss, cancelling,
}: {
  variant: 'compact' | 'full';
  onApprove: () => void;
  onReject: () => void;
  showCancelConfirm: boolean;
  onCancelClick: () => void;
  onCancelConfirm: () => void;
  onCancelDismiss: () => void;
  cancelling: boolean;
}) {
  const approveRejectCls = variant === 'compact'
    ? 'flex items-center gap-1 px-2.5 py-1.5'
    : 'flex items-center gap-1.5 px-3 py-1.5';
  const cancelLabel = variant === 'compact' ? 'Cancel' : 'Cancel booking';
  const confirmLabel = variant === 'compact' ? 'Confirm' : 'Confirm cancel';
  return (
    <>
      <button
        onClick={onApprove}
        className={`${approveRejectCls} rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
      </button>
      <button
        onClick={onReject}
        className={`${approveRejectCls} rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-100 transition-colors`}
      >
        <XCircle className="w-3.5 h-3.5" /> Reject
      </button>
      {!showCancelConfirm ? (
        <button
          onClick={onCancelClick}
          className="flex items-center gap-1 h-7 px-2.5 rounded-lg border border-slate-200 text-slate-500 text-xs font-medium hover:bg-slate-100 hover:border-slate-300 transition-colors"
        >
          <X className="w-3 h-3" /> {cancelLabel}
        </button>
      ) : (
        <div className="flex items-center gap-1.5">
          <button
            onClick={onCancelConfirm}
            disabled={cancelling}
            className="h-7 px-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold disabled:opacity-60 transition-colors"
          >
            {cancelling ? 'Cancelling…' : confirmLabel}
          </button>
          <button
            onClick={onCancelDismiss}
            className="h-7 w-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-100 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </>
  );
}

function ExpandedDetails({
  booking, customerStatusLabel, billingLabel, showActions, isPending,
  showCancelConfirm, setShowCancelConfirm, cancelling, onCancel, onReview,
}: {
  booking: StudioBooking;
  customerStatusLabel: string;
  billingLabel: string;
  showActions: boolean;
  isPending: boolean;
  showCancelConfirm: boolean;
  setShowCancelConfirm: (v: boolean) => void;
  cancelling: boolean;
  onCancel: () => void;
  onReview: (action: 'approved' | 'rejected') => void;
}) {
  return (
    <tr className="bg-slate-50">
      <td colSpan={8} className="px-6 pb-4 pt-0">
        <div className="p-4 bg-white rounded-xl border border-[#e2e8f0] flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {booking.goal && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Goal</p>
                <p className="text-sm text-navy">{booking.goal}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Details</p>
              <p className="text-sm text-navy">{customerStatusLabel} · {billingLabel}</p>
              {(booking.numberOfCustomers !== undefined || booking.numberOfAttendees !== undefined) && (
                <p className="text-xs text-slate-400 mt-0.5">
                  {[
                    booking.numberOfCustomers !== undefined && `${booking.numberOfCustomers} customer${booking.numberOfCustomers !== 1 ? 's' : ''}`,
                    booking.numberOfAttendees !== undefined && `${booking.numberOfAttendees} attendee${booking.numberOfAttendees !== 1 ? 's' : ''}`
                  ].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            {booking.reviewNotes && (
              <div className="sm:col-span-2">
                <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                  {booking.status === 'rejected' ? 'Rejection reason' : 'Review notes'}
                </p>
                <p className="text-sm text-navy">{booking.reviewNotes}</p>
                {booking.reviewedByName && <p className="text-xs text-slate-400 mt-0.5">— {booking.reviewedByName}</p>}
              </div>
            )}
          </div>
          {showActions && isPending && (
            <div className="flex items-center gap-2 pt-3 border-t border-[#e2e8f0]">
              <RowActions
                variant="full"
                onApprove={() => onReview('approved')}
                onReject={() => onReview('rejected')}
                showCancelConfirm={showCancelConfirm}
                onCancelClick={() => setShowCancelConfirm(true)}
                onCancelConfirm={onCancel}
                onCancelDismiss={() => setShowCancelConfirm(false)}
                cancelling={cancelling}
              />
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

function BookingRow({ booking, showActions, onRefresh }: {
  booking: StudioBooking; showActions: boolean; onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [reviewModal, setReviewModal] = useState<'approved' | 'rejected' | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [retryingSync, setRetryingSync] = useState(false);

  const isPending = booking.status === 'pending';
  const hasExpand = (showActions && isPending) || !!booking.goal || !!booking.reviewNotes;

  const customerStatusLabel = getCustomerStatusLabel(booking.customerStatus);
  const billingLabel = getBillingLabel(booking.billingType);

  async function handleCancel() {
    setCancelling(true);
    try { await cancelBooking(booking.bookingId); onRefresh(); }
    finally { setCancelling(false); setShowCancelConfirm(false); }
  }

  async function handleRetrySync() {
    setRetryingSync(true);
    try { await retryOutlookSync(booking.bookingId); onRefresh(); }
    catch { /* stays failed — badge remains, error kept server-side */ }
    finally { setRetryingSync(false); }
  }

  const dateLabel = booking.startDate === booking.endDate
    ? formatDate(booking.startDate)
    : `${formatDate(booking.startDate)} → ${formatDate(booking.endDate)}`;

  return (
    <>
      {reviewModal && (
        <ReviewModal
          booking={booking}
          action={reviewModal}
          onClose={() => setReviewModal(null)}
          onDone={() => { setReviewModal(null); onRefresh(); }}
        />
      )}
      <tr className="hover:bg-slate-50/60 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <StatusBadge status={booking.status} />
          <OutlookSyncBadge booking={booking} showActions={showActions} onRetry={handleRetrySync} retrying={retryingSync} />
          <OutlookConflictOverrideBadge booking={booking} />
        </td>
        <td className="px-4 py-3">
          <span className="font-semibold text-navy text-sm">{booking.customer}</span>
        </td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{booking.requestedByName}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{booking.roomName}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
          <div>{dateLabel}</div>
          <span className="inline-block mt-0.5 text-xs text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
            {DATE_TYPE_SHORT[booking.dateType]}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-slate-500">{booking.eventTypeName}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
          <FacilitatorCell name={booking.facilitatorName} tier={booking.facilitatorTier} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2 justify-end">
            {hasExpand && (
              <button
                onClick={() => setExpanded(e => !e)}
                className="text-slate-400 hover:text-navy p-1 rounded-lg hover:bg-slate-100 transition-colors"
                title="View details"
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
            {showActions && isPending && (
              <RowActions
                variant="compact"
                onApprove={() => setReviewModal('approved')}
                onReject={() => setReviewModal('rejected')}
                showCancelConfirm={showCancelConfirm}
                onCancelClick={() => setShowCancelConfirm(true)}
                onCancelConfirm={handleCancel}
                onCancelDismiss={() => setShowCancelConfirm(false)}
                cancelling={cancelling}
              />
            )}
          </div>
        </td>
      </tr>
      {expanded && hasExpand && (
        <ExpandedDetails
          booking={booking}
          customerStatusLabel={customerStatusLabel}
          billingLabel={billingLabel}
          showActions={showActions}
          isPending={isPending}
          showCancelConfirm={showCancelConfirm}
          setShowCancelConfirm={setShowCancelConfirm}
          cancelling={cancelling}
          onCancel={handleCancel}
          onReview={(action) => setReviewModal(action)}
        />
      )}
    </>
  );
}

// ============================================================================
// TABLE SHELL
// ============================================================================

function BookingsTable({
  bookings, showActions, onRefresh, showStatusCol = true
}: {
  bookings: StudioBooking[]; showActions: boolean; onRefresh: () => void; showStatusCol?: boolean;
}) {
  const [sort, setSort] = useState<SortState | null>(null);
  const [filters, setFilters] = useState<Filters>({ status: '', customer: '', requestedBy: '', room: '', eventType: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const rooms      = useMemo(() => [...new Set(bookings.map(b => b.roomName))].sort((a, b) => a.localeCompare(b)), [bookings]);
  const eventTypes = useMemo(() => [...new Set(bookings.map(b => b.eventTypeName))].sort((a, b) => a.localeCompare(b)), [bookings]);

  const filtered   = useMemo(() => applyFilters(bookings, filters), [bookings, filters]);
  const sorted     = useMemo(() => applySort(filtered, sort), [filtered, sort]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paginated  = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  function handleSort(col: SortCol) {
    setSort(prev => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null;
    });
    setPage(1);
  }

  function setFilter(key: keyof Filters, val: string) {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(1);
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  const colSpan = (showStatusCol ? 8 : 7);

  const inputCls = "h-8 rounded-lg border border-[#e2e8f0] bg-white px-2.5 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";

  return (
    <>
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        {/* Filter bar */}
        <div className="p-4 border-b border-[#e2e8f0]">
          <div className="flex flex-wrap gap-3 items-center">
            {showStatusCol && (
              <select value={filters.status} onChange={e => setFilter('status', e.target.value)} className={inputCls}>
                <option value="">All statuses</option>
                {(['pending', 'approved', 'rejected', 'cancelled'] as BookingStatus[]).map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            )}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
              <input type="text" placeholder="Customer" value={filters.customer}
                onChange={e => setFilter('customer', e.target.value)}
                className={`${inputCls} pl-6 w-36`} />
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
              <input type="text" placeholder="Requested by" value={filters.requestedBy}
                onChange={e => setFilter('requestedBy', e.target.value)}
                className={`${inputCls} pl-6 w-36`} />
            </div>
            <select value={filters.room} onChange={e => setFilter('room', e.target.value)} className={inputCls}>
              <option value="">All rooms</option>
              {rooms.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={filters.eventType} onChange={e => setFilter('eventType', e.target.value)} className={inputCls}>
              <option value="">All event types</option>
              {eventTypes.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            {hasActiveFilters && (
              <button
                onClick={() => { setFilters({ status: '', customer: '', requestedBy: '', room: '', eventType: '' }); setPage(1); }}
                className="h-8 px-2.5 text-xs text-slate-500 hover:text-navy flex items-center gap-1 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
            <span className="ml-auto text-xs text-slate-400">
              {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                {showStatusCol && <ColHeader col="status"      label="Status"       sort={sort} onSort={handleSort} />}
                <ColHeader col="customer"    label="Customer"     sort={sort} onSort={handleSort} />
                <ColHeader col="requestedBy" label="Requested by" sort={sort} onSort={handleSort} />
                <ColHeader col="room"        label="Room"         sort={sort} onSort={handleSort} />
                <ColHeader col="date"        label="Date"         sort={sort} onSort={handleSort} />
                <ColHeader col="eventType"   label="Event type"   sort={sort} onSort={handleSort} />
                <ColHeader col="facilitator" label="Facilitator"  sort={sort} onSort={handleSort} />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-12 text-center text-sm text-slate-400">
                    No bookings match the current filters.
                  </td>
                </tr>
              ) : (
                paginated.map(b => (
                  <BookingRow key={b.bookingId} booking={b} showActions={showActions} onRefresh={onRefresh} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Rows per page:
          {PAGE_SIZES.map(s => (
            <button key={s} onClick={() => { setPageSize(s); setPage(1); }}
              className={`px-2 py-1 rounded-lg transition-colors ${pageSize === s ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-slate-100'}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-400 mr-2">
            {sorted.length === 0 ? '0' : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, sorted.length)}`} of {sorted.length}
          </span>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
            className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
            .reduce<(number | '…')[]>((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => p === '…'
              ? <span key={`ellipsis-at-${i}`} className="px-1 text-xs text-slate-400">…</span> // NOSONAR
              : (
              <button key={p} onClick={() => setPage(p)}
                className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${safePage === p ? 'bg-primary text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                {p}
              </button>
            ))
          }
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
            className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronRight className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function ReviewQueue() {
  const [tab, setTab] = useState<'pending' | 'all'>('pending');
  const [pending, setPending] = useState<StudioBooking[]>([]);
  const [all, setAll] = useState<StudioBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    Promise.all([listReviewQueue(), listAllBookings()])
      .then(([p, a]) => { setPending(p); setAll(a); })
      .catch(() => setError('Failed to load bookings'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <ClipboardCheck size={22} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight text-navy">Review Queue</h1>
                {pending.length > 0 && (
                  <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200">
                    {pending.length}
                  </span>
                )}
              </div>
              <p className="text-slate-500 text-sm font-medium">Approve or reject studio booking requests</p>
            </div>
          </div>
        </header>

        {/* Error */}
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#e2e8f0] -mb-2">
          <button
            onClick={() => setTab('pending')}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px flex items-center gap-2 ${
              tab === 'pending' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-navy'
            }`}
          >
            Pending
            {pending.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">{pending.length}</span>
            )}
          </button>
          <button
            onClick={() => setTab('all')}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px flex items-center gap-2 ${
              tab === 'all' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-navy'
            }`}
          >
            <History className="w-4 h-4" /> All
          </button>
        </div>

        {/* Content */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}
        {!loading && tab === 'pending' && pending.length === 0 && (
          <div className="rounded-xl bg-white p-12 border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <ClipboardCheck size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">All caught up.</p>
            <p className="text-slate-400 text-xs mt-2">No bookings pending review.</p>
          </div>
        )}
        {!loading && tab === 'pending' && pending.length > 0 && (
          <BookingsTable bookings={pending} showActions showStatusCol={false} onRefresh={load} />
        )}
        {!loading && tab === 'all' && (
          <BookingsTable bookings={all} showActions={false} onRefresh={load} />
        )}

      </div>
    </div>
  );
}
